<template>
  <Container>
    <div class="ding-filter">
      <Select
        v-model="filter.processCode"
        filterable
        clearable
        placeholder="选择审批流程"
        style="width:260px;"
        :loading="loadingProcesses"
      >
        <Option
          v-for="p in processes"
          :key="p.processCode"
          :value="p.processCode"
          :label="`${p.name} (${p.processCode})`"
        />
      </Select>
      <Select v-model="filter.statuses" multiple style="width:220px;" placeholder="状态">
        <Option value="RUNNING">在途 RUNNING</Option>
        <Option value="COMPLETED">完成 COMPLETED</Option>
        <Option value="TERMINATED">撤销 TERMINATED</Option>
      </Select>
      <DatePicker
        v-model="filter.range"
        type="datetimerange"
        placement="bottom-start"
        placeholder="发起时间范围"
        style="width:300px;"
      />
      <Input
        v-model="filter.userIds"
        clearable
        style="width:180px;"
        placeholder="发起人 userid，逗号分隔"
      />
      <Checkbox v-model="filter.detail">附带详情</Checkbox>
      <Checkbox v-model="filter.all">拉全量</Checkbox>
      <Button type="primary" :loading="loading" :disabled="!filter.processCode" @click="loadData">查询</Button>
      <Button icon="md-refresh" :loading="loadingProcesses" @click="loadProcesses">流程列表</Button>
    </div>

    <div class="ding-meta" v-if="meta.count != null">
      <span>共 {{ meta.count }} 条</span>
      <span v-if="meta.nextToken">nextToken: {{ meta.nextToken }}</span>
      <Button v-if="meta.nextToken && !filter.all" size="small" :loading="loadingMore" @click="loadMore">加载下一页</Button>
    </div>

    <div class="ding-table-wrap">
      <BaseTable
        :columns="columns"
        :data="tableRows"
        :loading="loading"
        border
      />
    </div>

    <Drawer v-model="detail.visible" title="实例详情" width="640" :mask-closable="true">
      <div v-if="detail.data" class="ding-detail">
        <div class="ding-info-grid">
          <div class="ding-info-item"><label>标题</label><span>{{ detail.data.title || '-' }}</span></div>
          <div class="ding-info-item"><label>实例 ID</label><span>{{ detail.data.processInstanceId || detail.id }}</span></div>
          <div class="ding-info-item"><label>状态</label>
            <Tag :color="statusColor(detail.data.status)">{{ detail.data.status || '-' }}</Tag>
          </div>
          <div class="ding-info-item"><label>结果</label><span>{{ detail.data.result || '-' }}</span></div>
          <div class="ding-info-item">
            <label>发起人</label>
            <a
              v-if="detailOriginatorId"
              class="ding-link"
              @click="openUserDrawer(detailOriginatorId)"
            >{{ formatOriginator(detailOriginatorId) }}</a>
            <span v-else>-</span>
          </div>
          <div class="ding-info-item"><label>创建时间</label><span>{{ formatTime(detail.data.createTime || detail.data.create_time) }}</span></div>
          <div class="ding-info-item"><label>完成时间</label><span>{{ formatTime(detail.data.finishTime || detail.data.finish_time) }}</span></div>
        </div>

        <h4 style="margin:20px 0 10px;">表单字段</h4>
        <BaseTable
          v-if="formRows.length"
          :columns="formColumns"
          :data="formRows"
          border
          size="small"
        />
        <BaseEmpty v-else msg="暂无表单字段" />

        <div class="ding-detail-footer">
          <Button type="primary" @click="goRecord(detail.id || detail.data.processInstanceId)">查看流程数据</Button>
          <Button style="margin-left:8px;" @click="detail.visible = false">关闭</Button>
        </div>
      </div>
    </Drawer>

    <DingUserDrawer
      v-model="userDrawer.visible"
      :userid="userDrawer.userid"
      @open-dept="openDeptDrawer"
      @open-user="openUserDrawer"
    />
    <DingDeptDrawer
      v-model="deptDrawer.visible"
      :dept-id="deptDrawer.deptId"
      @open-user="openUserDrawer"
      @open-dept="openDeptDrawer"
    />

    <BaseLoadingModal v-model="loading" title="正在查询流程实例..." />
  </Container>
</template>

<script>
import Container from '@/components/app/container.vue';
import moment from 'moment';
import {
  ensureDingDirectory,
  formatUserLabel,
  getDeptName,
  getUserName,
  resolveDirectoryRefs,
} from './ding-directory';
import DingUserDrawer from './DingUserDrawer.vue';
import DingDeptDrawer from './DingDeptDrawer.vue';

export default {
  name: 'DingInstances',
  components: {Container, DingUserDrawer, DingDeptDrawer},
  data() {
    return {
      loading: false,
      loadingMore: false,
      loadingProcesses: false,
      processes: [],
      ids: [],
      details: [],
      dirTick: 0,
      filter: {
        processCode: '',
        statuses: ['RUNNING'],
        range: [],
        userIds: '',
        detail: true,
        all: false,
      },
      meta: {
        count: null,
        nextToken: null,
      },
      detail: {
        visible: false,
        id: '',
        data: null,
      },
      userDrawer: {
        visible: false,
        userid: '',
      },
      deptDrawer: {
        visible: false,
        deptId: '',
      },
      formColumns: [{
        title: '字段',
        key: 'name',
        minWidth: 120,
      }, {
        title: '类型',
        key: 'componentType',
        width: 120,
      }, {
        title: '值',
        key: 'value',
        minWidth: 180,
        render: (h, {row}) => this.renderMatchedValue(h, row.value),
      }],
    };
  },
  computed: {
    detailOriginatorId() {
      const d = this.detail.data || {};
      return d.originatorUserId || d.originator_userid || '';
    },
    tableRows() {
      if (this.details.length) {
        return this.details.map((d, i) => ({
          ...d,
          _id: d.processInstanceId || this.ids[i] || String(i),
        }));
      }
      return this.ids.map((id) => ({
        _id: id,
        processInstanceId: id,
        title: id,
        status: '-',
      }));
    },
    formRows() {
      const data = this.detail.data || {};
      const vals = data.formComponentValues || data.form_component_values || [];
      return Array.isArray(vals) ? vals : [];
    },
    columns() {
      void this.dirTick;
      return [{
        type: 'index',
        width: 60,
      }, {
        title: '标题',
        key: 'title',
        minWidth: 180,
        render: (h, {row}) => h('a', {
          style: {color: 'var(--primary)', cursor: 'pointer'},
          on: {click: () => this.openDetail(row)},
        }, row.title || row.processInstanceId || row._id),
      }, {
        title: '实例 ID',
        key: 'processInstanceId',
        minWidth: 220,
        render: (h, {row}) => h('span', row.processInstanceId || row._id),
      }, {
        title: '状态',
        key: 'status',
        width: 120,
        render: (h, {row}) => h('Tag', {
          props: {color: this.statusColor(row.status)},
        }, row.status || '-'),
      }, {
        title: '结果',
        key: 'result',
        width: 100,
      }, {
        title: '发起人',
        key: 'originatorUserId',
        minWidth: 160,
        render: (h, {row}) => {
          const id = row.originatorUserId || row.originator_userid;
          if (!id) return h('span', '-');
          return h('a', {
            style: {color: 'var(--primary)', cursor: 'pointer'},
            on: {click: () => this.openUserDrawer(id)},
          }, formatUserLabel(id));
        },
      }, {
        title: '发起时间',
        key: 'createTime',
        width: 160,
        render: (h, {row}) => h('span', this.formatTime(row.createTime || row.create_time)),
      }, {
        title: '操作',
        width: 180,
        fixed: 'right',
        render: (h, {row}) => h('div', {class: 'flex-wrap flex-center split5'}, [
          h('Button', {
            props: {size: 'small', type: 'primary'},
            on: {click: () => this.openDetail(row)},
          }, '详情'),
          h('Button', {
            props: {size: 'small'},
            on: {click: () => this.goRecord(row.processInstanceId || row._id)},
          }, '表单'),
        ]),
      }];
    },
  },
  mounted() {
    this.$api.afterLogin(async () => {
      try {
        await ensureDingDirectory(this.$api);
        this.dirTick += 1;
      } catch (e) { /* 通讯录失败不阻断流程查询 */ }
      await this.loadProcesses();
      this.applyRouteQuery(true);
    });
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.applyRouteQuery(false);
      },
    },
  },
  methods: {
    unwrap(res) {
      return (res && res.data && res.data.data) || (res && res.data) || {};
    },
    applyRouteQuery(autoLoad) {
      const q = this.$route.query || {};
      let changed = false;
      if (q.processCode && q.processCode !== this.filter.processCode) {
        this.filter.processCode = q.processCode;
        changed = true;
      }
      if (q.statuses) {
        const list = String(q.statuses).split(',').map((s) => s.trim()).filter(Boolean);
        if (list.length) {
          this.filter.statuses = list;
          changed = true;
        }
      }
      if ((autoLoad || changed) && this.filter.processCode) {
        this.loadData();
      }
    },
    stringifyValue(v) {
      if (v == null || v === '') return '-';
      if (typeof v === 'object') {
        try { return JSON.stringify(v); } catch (e) { return String(v); }
      }
      return String(v);
    },
    formatOriginator(id) {
      return formatUserLabel(id);
    },
    openUserDrawer(userid) {
      if (userid == null || userid === '') return;
      this.userDrawer.userid = String(userid);
      this.userDrawer.visible = true;
    },
    openDeptDrawer(deptId) {
      if (deptId == null || deptId === '') return;
      this.deptDrawer.deptId = deptId;
      this.deptDrawer.visible = true;
    },
    renderMatchedValue(h, value) {
      const refs = resolveDirectoryRefs(value);
      if (!refs.users.length && !refs.depts.length) {
        return h('span', this.stringifyValue(value));
      }
      const nodes = [];
      refs.users.forEach((id) => {
        if (nodes.length) nodes.push(h('span', {style: {color: 'var(--text2)'}}, '、'));
        nodes.push(h('a', {
          class: 'ding-link',
          style: {color: 'var(--primary)', cursor: 'pointer'},
          on: {click: () => this.openUserDrawer(id)},
        }, getUserName(id) || id));
      });
      refs.depts.forEach((id) => {
        if (nodes.length) nodes.push(h('span', {style: {color: 'var(--text2)'}}, '、'));
        nodes.push(h('a', {
          class: 'ding-link',
          style: {color: 'var(--primary)', cursor: 'pointer'},
          on: {click: () => this.openDeptDrawer(id)},
        }, getDeptName(id) || id));
      });
      return h('span', nodes);
    },
    formatTime(t) {
      if (!t) return '-';
      const m = moment(t);
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(t);
    },
    statusColor(status) {
      const map = {
        RUNNING: 'blue',
        COMPLETED: 'green',
        TERMINATED: 'default',
      };
      return map[status] || 'default';
    },
    buildQuery(extra = {}) {
      const params = [`processCode=${encodeURIComponent(this.filter.processCode)}`];
      if (this.filter.statuses && this.filter.statuses.length) {
        params.push(`statuses=${encodeURIComponent(this.filter.statuses.join(','))}`);
      }
      if (this.filter.userIds) {
        params.push(`userIds=${encodeURIComponent(this.filter.userIds)}`);
      }
      if (this.filter.range && this.filter.range[0]) {
        let startMs = moment(this.filter.range[0]).valueOf();
        let endMs = this.filter.range[1]
          ? moment(this.filter.range[1]).valueOf()
          : Date.now();
        // 钉钉限制：end >= start，且跨度不超过 120 天
        if (endMs < startMs) {
          const tmp = startMs;
          startMs = endMs;
          endMs = tmp;
        }
        const maxSpan = 120 * 24 * 3600 * 1000;
        if (endMs - startMs > maxSpan) {
          startMs = endMs - maxSpan;
        }
        params.push(`startTime=${startMs}`);
        params.push(`endTime=${endMs}`);
      }
      if (this.filter.detail) params.push('detail=1');
      if (this.filter.all) params.push('all=1');
      Object.keys(extra).forEach((k) => {
        if (extra[k] != null && extra[k] !== '') params.push(`${k}=${encodeURIComponent(extra[k])}`);
      });
      return params.join('&');
    },
    async loadProcesses() {
      this.loadingProcesses = true;
      try {
        const res = await this.$api.get('/ding/processes?all=1');
        const data = this.unwrap(res);
        this.processes = Array.isArray(data.list) ? data.list : [];
      } catch (e) {
        this.Error(e);
      } finally {
        this.loadingProcesses = false;
      }
    },
    async loadData() {
      if (!this.filter.processCode) {
        this.$Message.warning('请先选择审批流程');
        return;
      }
      this.loading = true;
      try {
        const res = await this.$api.get(`/ding/instances?${this.buildQuery()}`);
        const data = this.unwrap(res);
        this.ids = Array.isArray(data.list) ? data.list : [];
        this.details = Array.isArray(data.details) ? data.details : [];
        this.meta.count = data.count != null ? data.count : this.ids.length;
        this.meta.nextToken = data.nextToken || null;
      } catch (e) {
        this.Error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      if (!this.meta.nextToken) return;
      this.loadingMore = true;
      try {
        const res = await this.$api.get(`/ding/instances?${this.buildQuery({nextToken: this.meta.nextToken})}`);
        const data = this.unwrap(res);
        const moreIds = Array.isArray(data.list) ? data.list : [];
        const moreDetails = Array.isArray(data.details) ? data.details : [];
        this.ids = this.ids.concat(moreIds);
        this.details = this.details.concat(moreDetails);
        this.meta.count = this.ids.length;
        this.meta.nextToken = data.nextToken || null;
      } catch (e) {
        this.Error(e);
      } finally {
        this.loadingMore = false;
      }
    },
    async openDetail(row) {
      const id = row.processInstanceId || row._id;
      this.detail.id = id;
      this.detail.visible = true;
      if (row.formComponentValues || row.form_component_values || row.title) {
        this.detail.data = row;
      } else {
        this.detail.data = {processInstanceId: id};
      }
      try {
        const res = await this.$api.get(`/ding/instances/${encodeURIComponent(id)}`);
        this.detail.data = this.unwrap(res) || this.detail.data;
      } catch (e) {
        this.Error(e);
      }
    },
    goRecord(id) {
      if (!id) return;
      this.RouteTo(`records?processInstanceId=${encodeURIComponent(id)}`);
    },
  },
};
</script>

<style lang="less" scoped>
.ding-filter {
  flex-shrink: 0;
  box-sizing: border-box;
  min-height: 50px;
  padding: 8px 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}
.ding-meta {
  flex-shrink: 0;
  box-sizing: border-box;
  min-height: 36px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: var(--text2);
  border-bottom: 1px solid var(--border);
}
.ding-table-wrap {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow: hidden;
  position: relative;
}
.ding-info-grid {
  display: grid;
  gap: 12px;
}
.ding-info-item {
  display: flex;
  align-items: flex-start;
  label {
    width: 80px;
    color: var(--text2);
    flex-shrink: 0;
  }
  span {
    word-break: break-all;
  }
}
.ding-detail-footer {
  margin-top: 24px;
  text-align: right;
}
.ding-link {
  color: var(--primary);
  cursor: pointer;
}
</style>
